/* eslint-disable no-console */

export interface WebSocketProgressMessage {
   type: 'progress';
   stage: 'received' | 'planning' | 'acting' | 'observing' | 'generating' | 'complete';
   message: string;
   status: string;
   data?: Record<string, unknown>;
}

export interface WebSocketAnswerMessage {
   type: 'answer' | 'cancelled';
   response?: string;
   request?: string;
   reasoning?: string;
   duration_seconds?: number;
   reference?: { document_id: string; file_name: string; page_number: number }[];
   filter?: Record<string, string | number | Date>[];
   number_of_documents?: number | null;
   session_id?: string;
   warnings?: string[] | null;
   status: string;
}

export interface WebSocketChatCallbacks {
   onProgress: (message: WebSocketProgressMessage) => void;
   onAnswer: (message: WebSocketAnswerMessage) => void;
   onError: (error: string) => void;
   onClose?: (code: number, reason: string) => void;
}

export type WebSocketSendPayload = { message: string } & Record<never, never>;

export interface WebSocketChatConnection {
   sendMessage: (message: string) => void;
   cancel: () => void;
   close: () => void;
}

export interface CreateWebSocketChatOptions {
   wsBaseUrl: string;
   apiKey: string;
   threadId?: number;
   docids?: string[];
   callbacks: WebSocketChatCallbacks;
}

export function createWebSocketChatConnection(
   options: CreateWebSocketChatOptions,
): WebSocketChatConnection {
   const { wsBaseUrl, apiKey, threadId, docids, callbacks } = options;

   console.log('[WS] Creating WebSocket connection', { wsBaseUrl, threadId });

   const url = new URL(wsBaseUrl);
   url.searchParams.append('api_key', apiKey);
   if (threadId != null && threadId !== 0) {
      url.searchParams.append('thread_id', String(threadId));
   }
   if (docids != null && docids.length > 0) {
      url.searchParams.append('docids', docids.join(','));
   }

   const wsUrl = url.toString();
   console.log('[WS] WebSocket URL:', wsUrl);

   const ws = new WebSocket(wsUrl);
   console.log('[WS] WebSocket object created, readyState:', ws.readyState);
   const pendingMessages: string[] = [];

   ws.onopen = () => {
      console.log('[WS] Connection opened, sending', pendingMessages.length, 'pending messages');
      for (const msg of pendingMessages) {
         console.log('[WS] Sending pending message:', msg);
         ws.send(msg);
      }
      pendingMessages.length = 0;
   };

   ws.onmessage = (event: MessageEvent) => {
      console.log('[WS] Received message:', event.data);
      let data: Record<string, unknown>;
      try {
         data = JSON.parse(event.data as string);
         console.log('[WS] Parsed message:', data);
      }
      catch (e) {
         console.error('[WS] Failed to parse message:', event.data, e);
         callbacks.onError('Failed to parse server message');
         return;
      }

      switch (data.type) {
         case 'progress':
            console.log('[WS] Progress message:', data);
            callbacks.onProgress(data as unknown as WebSocketProgressMessage);
            break;
         case 'answer':
            console.log('[WS] Answer message:', data);
            callbacks.onAnswer(data as unknown as WebSocketAnswerMessage);
            break;
         case 'cancelled':
            console.log('[WS] Cancelled message:', data);
            callbacks.onAnswer(data as unknown as WebSocketAnswerMessage);
            break;
         case 'error':
            console.error('[WS] Error message:', data);
            callbacks.onError(typeof data.error === 'string' ? data.error : 'Unknown server error');
            break;
         default:
            console.error('[WS] Unknown message type:', data.type, data);
            callbacks.onError(`Unknown message type: ${String(data.type)}`);
            break;
      }
   };

   ws.onerror = () => {
      console.error('[WS] WebSocket error, readyState:', ws.readyState);
      pendingMessages.length = 0;
      callbacks.onError('WebSocket connection error');
   };

   ws.onclose = (event: CloseEvent) => {
      console.log('[WS] Connection closed, code:', event.code, 'reason:', event.reason);
      pendingMessages.length = 0;
      callbacks.onClose?.(event.code, event.reason);
   };

   function safeSend(payload: string) {
      console.log('[WS] safeSend called, readyState:', ws.readyState, 'payload:', payload);
      if (ws.readyState === WebSocket.OPEN) {
         console.log('[WS] Sending message immediately');
         ws.send(payload);
      }
      else if (ws.readyState === WebSocket.CONNECTING) {
         console.log('[WS] Connection still connecting, queueing message');
         pendingMessages.push(payload);
      }
      else {
         console.error('[WS] WebSocket is closed (readyState:', `${ws.readyState})`);
         callbacks.onError('WebSocket is closed');
      }
   }

   return {
      sendMessage(message: string) {
         const payload: WebSocketSendPayload = { message };
         safeSend(JSON.stringify(payload));
      },
      cancel() {
         safeSend(JSON.stringify({ type: 'cancel' }));
      },
      close() {
         ws.close();
      },
   };
}
